/**
 * Profile Event Bus - Synchronizes profile updates across components
 *
 * This event bus ensures that when a profile is updated (e.g., profile picture),
 * all components displaying user data are immediately updated.
 */

type ProfileEventType = 'profile:updated';

interface ProfileUpdatedEvent {
  userId: string;
}

type ProfileEventData = ProfileUpdatedEvent;

type EventListener = (data: ProfileEventData) => void;

class ProfileEventBus {
  private listeners: Map<ProfileEventType, Set<EventListener>> = new Map();

  /**
   * Subscribe to profile events
   */
  on(event: ProfileEventType, callback: EventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from profile events
   */
  off(event: ProfileEventType, callback: EventListener): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit a profile event to all subscribers
   */
  emit(event: ProfileEventType, data: ProfileEventData): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in profile event listener:', error);
        }
      });
    }
  }

  /**
   * Emit a profile updated event
   */
  emitProfileUpdated(userId: string): void {
    this.emit('profile:updated', { userId });
  }

  /**
   * Clear all listeners (useful for cleanup/testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

// Export singleton instance
export const profileEventBus = new ProfileEventBus();

// Export types for use in components
export type {
  ProfileEventType,
  ProfileUpdatedEvent,
  ProfileEventData,
  EventListener
};
